import { SurveyEventSubmit, SurveyEventEmailTemplateEnum, SurveyEventStatusEnum } from "../models/survey.event.model";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { titleStr } from "../lib/helper";
import { FilterGroup, FilterType } from "../components/TableFilter";
import { HeadCell, Pagination } from "../models/base.model";
import {
  deleteSurveyEventSubmit,
  getAllRecordsUrl,
  getSubmitAnswers,
  getSurveyEventSubmitsById,
  sendSurveyEventEmails,
} from "../actions/survey.event.action";
import { Customer } from "../models/customer.model";
import { getCustomers } from "../actions/customer.action";
import { Chip, Stack } from "@mui/material";
import TooltipForForm from "../components/TooltipForForm";
import { getItemsNode } from "../components/EllipsisText";
import moment from "moment";
import LoadingButton from "../components/LoadingButton";
import { Question } from "../models/question.model";
import { getQuestions } from "../actions/question.action";
import SurveySubmitAnswerView from "./SurveySubmitAnswerView";
import { getCompanyStoresByCompanyId } from "../actions/company.store.action";
import { CompanyStore } from "../models/company.store.model";
import PaginatedTableList from "../components/PaginatedTableList";
import { openWindowInAsync } from "../actions/common.action";

const status_type_color_map: { [key in SurveyEventStatusEnum]: any } = {
  draft: "default",
  sent: "warning",
  viewed: "info",
  answered: "success",
};

const headCells: readonly HeadCell[] = [
  {
    id: "uid",
    label: "Customer ID",
  },
  {
    id: "name",
    label: "Customer Name",
  },
  {
    id: "device",
    label: "Device Info",
    render: (v) => {
      let device;
      try {
        if (v) {
          device = JSON.parse(v);
        }
      } catch (error) {
        console.warn("parse device fail", error);
      }
      return (
        <TooltipForForm title={getItemsNode(v)}>
          <span>{device?.vendor}</span>
        </TooltipForForm>
      );
    },
  },
  {
    id: "status",
    label: "Status",
    render: (v: SurveyEventStatusEnum) => <Chip color={status_type_color_map[v]} label={titleStr(v)} data-testid={titleStr(v)} />,
  },
  {
    id: "date",
    label: "Date",
    render: (_, record) => (
      <TooltipForForm
        title={
          <Stack gap={1}>
            <span>Viewed At: {record.viewedAt ? moment(record.viewedAt).format("YYYY-MM-DD hh:mm:ss") : "-"}</span>
            <span>Answered At: {record.answeredAt ? moment(record.answeredAt).format("YYYY-MM-DD hh:mm:ss") : "-"}</span>
            <span>Sent At: {record.sentAt ? moment(record.sentAt).format("YYYY-MM-DD hh:mm:ss") : "-"}</span>
          </Stack>
        }
      >
        <span>
          {record.answeredAt
            ? `Answered At: ${moment(record.answeredAt).format("YYYY-MM-DD")}`
            : record.viewedAt
            ? `Viewed At: ${moment(record.viewedAt).format("YYYY-MM-DD")}`
            : ""}
        </span>
      </TooltipForForm>
    ),
  },
  {
    id: "invalid",
    label: "Invalid",
    render: (_, record) => {
      if (record.invalid && record.invalidReason) {
        return (
          <TooltipForForm
            title={
              <Stack px={2} py={1} gap={1} component="ul" sx={{ listStyle: "decimal" }}>
                <li>{record.invalidReason}</li>
              </Stack>
            }
          >
            <Chip color="error" label="Invalid" data-testid={"Invalid"} />
          </TooltipForForm>
        );
      } else if (record?.invalid === false) {
        return <Chip color="success" label="Valid" data-testid={"Valid"} />;
      }
      return <div></div>;
    },
  },
  {
    id: "answers",
    label: "Answers",
    render: (_, submit) => (
      <LoadingButton
        variant="contained"
        onClick={async () => {
          const answers = await getSubmitAnswers({
            eventId: submit.surveyEventId,
            submitId: submit.id,
          });
          await window.webApp.toggleDialog({
            title: `Survey Answer`,
            element: (
              <SurveySubmitAnswerView questions={cached_questions || []} answers={answers} store={cached_stores?.get(submit.storeId)} />
            ),
            minWidth: 800,
            onOk: async () => {},
            okText: "Close",
            noCancel: true,
            open: true,
          });
        }}
      >
        View
      </LoadingButton>
    ),
  },
];

const filters: FilterGroup<SurveyEventSubmit>[] = [
  {
    title: "Filter By Date",
    items: [
      {
        name: "viewedAt",
        type: FilterType.date_range,
      },
      {
        name: "sentAt",
        type: FilterType.date_range,
      },
      {
        name: "answeredAt",
        type: FilterType.date_range,
      },
    ],
  },
  {
    title: "Filter By Status",
    items: [
      {
        name: "status",
        type: FilterType.selection,
        options: Object.values(SurveyEventStatusEnum).map((status) => ({
          label: titleStr(status),
          value: status,
        })),
        multiple: true,
      },
      {
        name: "invalid",
        type: FilterType.selection,
        options: [
          {
            label: "Valid",
            value: "false",
          },
          {
            label: "Invalid",
            value: "true",
          },
        ],
      },
    ],
  },
];

let cached_customers: Customer[] | undefined;
let cached_questions: Question[] | undefined;
let cached_stores: Map<string, CompanyStore> | undefined;

export default forwardRef((props: { id: string; customers?: SurveyEventSubmit[]; companyId: string }, ref) => {
  const { id, companyId } = props;
  const tableRef = useRef<any>(null);

  useEffect(() => {
    load();
    return () => {
      cached_customers = undefined;
      cached_questions = undefined;
      cached_stores = undefined;
    };
  }, []);

  useImperativeHandle(ref, () => ({
    onSubmit() {
      return Promise.resolve([]);
    },
  }));

  const load = async () => {
    const [customers, questions, stores] = await Promise.all([
      getCustomers(),
      getQuestions(),
      getCompanyStoresByCompanyId(companyId, {
        isAll: true,
        attributes: ["pk", "name", "storeId"],
      }),
    ]);
    cached_customers = customers.data;
    cached_questions = questions.data;
    cached_stores = stores.data.reduce((map, store) => {
      map.set(store.id, store);
      return map;
    }, new Map<string, CompanyStore>());
  };

  const loadData = async (param: Pagination) => {
    if (param.filter?.and) {
      param.filter.and = param.filter.and.map((condition) => {
        if (condition.key === "invalid") {
          return {
            ...condition,
            values: condition.values.map((v) => v === "true"),
          };
        }
        return condition;
      });
    }
    const res: any = await getSurveyEventSubmitsById({
      id,
      attributes: ["pk", "sk", "data", "uid", "name", "device", "status", "viewedAt", "answeredAt", "sentAt", "invalid", "invalidReason"],
      ...param,
    });
    return res;
  };

  const onDelete = async (record: SurveyEventSubmit) => {
    await deleteSurveyEventSubmit(record.id, record.companyId);
  };

  const loadAllSubmits = async () => {
    let offset = undefined;
    let data: SurveyEventSubmit[] = [];
    do {
      const res: any = await getSurveyEventSubmitsById({
        id,
        offset,
        limit: 500,
      });
      data = data.concat(res.data);
      offset = res.offset;
    } while (offset);
    return data;
  };

  const downloadAllRecords = async () => {
    openWindowInAsync(async () => {
      const url = await getAllRecordsUrl({ id, companyId });
      return url;
    }, "Downloading records..., please do not close this window.");
  };

  return (
    <div>
      <Stack direction={"row"} spacing={2}>
        <LoadingButton
          onClick={async () => {
            const allData = await loadAllSubmits();
            const data = await sendSurveyEventEmails({
              id,
              customerEmails: allData
                .filter((a) => a.status === SurveyEventStatusEnum.draft && a.email)
                .map((a) => ({
                  email: a.email!,
                  template: SurveyEventEmailTemplateEnum.send,
                })),
            });
            tableRef?.current?.onRefresh?.(data);
          }}
        >
          Send Email To All Draft Status
        </LoadingButton>
        <LoadingButton onClick={downloadAllRecords}>Download All Records</LoadingButton>
      </Stack>
      <PaginatedTableList<SurveyEventSubmit>
        headCells={headCells}
        ref={tableRef}
        uid="uid"
        addButtonClassName="survey-customer-add-btn"
        onLoad={loadData}
        filters={filters}
        initialSortKey={"answeredAt"}
        initialOrder={"desc"}
        onDelete={onDelete}
      />
    </div>
  );
});
